import { Check, User } from '@huspy/briks-icons';
import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@huspy/briks-web';
import { SectionHeader } from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import { UseFormReturnType } from '@mantine/form';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';

const baseTranslation = 'clients.assistedStatusInfo';

type AssistedCardProps = {
  isActive: boolean;
  onClick: () => void;
  isDisabled: boolean;
  children: React.ReactNode;
};

const AssistedCard = ({
  isActive,
  onClick,
  isDisabled,
  children,
}: AssistedCardProps) => (
  <Flex
    p='4'
    w='50%'
    direction='column'
    bg={ isActive ? 'indigo.50' : 'neutral.100' }
    borderRadius='4'
    borderColor={ isActive ? 'indigo.400' : 'base.white' }
    borderWidth='1'
    cursor={ isDisabled ? 'not-allowed' : 'pointer' }
    onClick={ !isDisabled ? onClick : undefined }
    data-testid='toggle-assist-btn'
  >
    <Flex w='100%'>
      <Flex
        justify='center'
        align='center'
        ml='auto'
        p='1'
        w='5'
        h='5'
        borderRadius='50%'
        bg={ isActive ? 'black' : 'transparent' }
        borderWidth={ !isActive ? '1' : undefined }
        borderColor={ !isActive ? 'black' : undefined }
      >
        {isActive && <Check color='white' height='20px' width='20px' />}
      </Flex>
    </Flex>
    <Flex direction='column' align='flex-start'>
      {children}
    </Flex>
  </Flex>
);

type AssistedStatusInfoProps = {
  form: UseFormReturnType<any>;
  isSomeCasesSubmitted: boolean;
  isCommercial: boolean;
};

export const AssistedStatusInfo = ({
  form,
  isSomeCasesSubmitted,
  isCommercial,
}: AssistedStatusInfoProps) => {
  const { t } = useTranslation();
  const isAssisted = form.getValues().is_assisted === true;
  const { isFeatureActive } = useFeatureFlags();
  const isMortgageUsageActive = isFeatureActive('opportunities.mortgageUsage');
  const context = isCommercial ? 'commercial' : 'residential';

  const handleClick = (value: boolean) => {
    if (!isSomeCasesSubmitted) {
      form.setFieldValue('is_assisted', value);
    }
  };

  const getCardContent = (titleKey: string, descKey: string) => (
    <>
      <Text
        textAlign='center'
        size='lg'
        fontWeight='semibold'
        color={ isSomeCasesSubmitted ? 'neutral.500' : 'neutral.800' }
      >
        {t(`${baseTranslation}.${titleKey}`)}
      </Text>
      <Text
        size='md'
        color={ isSomeCasesSubmitted ? 'neutral.500' : 'neutral.800' }
      >
        {t(`${baseTranslation}.${descKey}`)}
      </Text>
    </>
  );

  if (!isMortgageUsageActive) {
    return <></>;
  }

  return (
    <>
      <SectionHeader title={ t(`${baseTranslation}.${context}`) } Icon={ User } />
      <Flex gap='2'>
        <AssistedCard
          isActive={ isAssisted }
          onClick={ () => handleClick(true) }
          isDisabled={ isSomeCasesSubmitted }
        >
          {getCardContent('assistedCase', 'assistedCaseDesc')}
        </AssistedCard>
        <AssistedCard
          isActive={ !isAssisted }
          onClick={ () => handleClick(false) }
          isDisabled={ isSomeCasesSubmitted }
        >
          {getCardContent('notAssistedCase', 'notAssistedCaseDesc')}
        </AssistedCard>
      </Flex>
    </>
  );
};
